
import { updateMetaTags } from './seoUtils';
import { generateEnhancedSchema } from './accessibleSEO';
import { generateIndustryServiceSchema } from './seoSchema';

/**
 * Sets up SEO for the root language routes (/:lang)
 * This ensures proper meta tags and schema for the landing page
 */
export const setupRootRouteSeo = (language: string): void => {
  // Define SEO content based on language with enhanced keyword optimization
  const getSeoTitle = () => {
    switch (language) {
      case 'de': return 'KI-Video Marketing für Unternehmen | Future Level Schweiz | Maßgeschneiderte Video-Lösungen';
      case 'en': return 'AI Video Marketing for Businesses | Future Level Switzerland | Custom Video Solutions';
      case 'fr': return 'Marketing vidéo IA pour entreprises | Future Level Suisse | Solutions vidéo personnalisées';
      case 'it': return 'Video marketing AI per aziende | Future Level Svizzera | Soluzioni video personalizzate';
      default: return 'KI-Video Marketing für Unternehmen | Future Level Schweiz | Maßgeschneiderte Video-Lösungen';
    }
  };
  
  const getSeoDescription = () => {
    switch (language) {
      case 'de': return 'Professionelle KI-Marketingvideos für mehr Conversion - Future Level revolutioniert Ihren Social Media Auftritt mit automatisierten Video-Lösungen und KI-generierten Inhalten für Schweizer Unternehmen.';
      case 'en': return 'Professional AI marketing videos for higher conversion rates - Future Level revolutionizes your social media presence with automated video solutions and AI-generated content for Swiss businesses.';
      case 'fr': return 'Vidéos marketing IA professionnelles pour un meilleur taux de conversion - Future Level révolutionne votre présence sur les médias sociaux avec des solutions vidéo automatisées et du contenu généré par IA pour les entreprises suisses.';
      case 'it': return 'Video di marketing AI professionali per tassi di conversione più elevati - Future Level rivoluziona la tua presenza sui social media con soluzioni video automatizzate e contenuti generati da IA per aziende svizzere.';
      default: return 'Professionelle KI-Marketingvideos für mehr Conversion - Future Level revolutioniert Ihren Social Media Auftritt mit automatisierten Video-Lösungen und KI-generierten Inhalten für Schweizer Unternehmen.';
    }
  };
  
  // Enhanced keywords from the research document
  const getKeywords = () => {
    switch (language) {
      case 'de': return 'KI Video Marketing, AI Videoproduktion, Social Media Videos, Produktvideos KI, Schweiz, Future Level, KI-Videoproduktion Schweiz, Automatisierte Videoerstellung, Content-Marketing KI, Kostengünstige KI-Videos';
      case 'en': return 'AI Video Marketing, AI Video Production, Social Media Videos, Product Videos AI, Switzerland, Future Level, AI Video Production Switzerland, Automated Video Creation, Content Marketing AI, Cost-effective AI Videos';
      case 'fr': return 'Marketing vidéo IA, Production vidéo IA, Vidéos médias sociaux, Vidéos produits IA, Suisse, Future Level, Production vidéo IA Suisse, Création vidéo automatisée, Marketing de contenu IA, Vidéos IA rentables';
      case 'it': return 'Marketing video AI, Produzione video AI, Video social media, Video prodotti AI, Svizzera, Future Level, Produzione video AI Svizzera, Creazione video automatizzata, Marketing contenuti AI, Video AI economici';
      default: return 'KI Video Marketing, AI Videoproduktion, Social Media Videos, Produktvideos KI, Schweiz, Future Level, KI-Videoproduktion Schweiz, Automatisierte Videoerstellung, Content-Marketing KI, Kostengünstige KI-Videos';
    }
  };
  
  // Base URL for canonical and structured data
  const baseUrl = 'https://future-level-video-craft.lovable.app';
  const canonicalUrl = `${baseUrl}/${language}`;
  
  // Update meta tags including OpenGraph and Twitter cards with enhanced keywords
  updateMetaTags({
    title: getSeoTitle(),
    description: getSeoDescription(),
    keywords: getKeywords(),
    canonicalUrl: canonicalUrl,
    ogImage: "https://future-level.ch/opengraph-image-p98pqg.png",
    ogType: "website",
    language: language
  });
  
  // Add schema.org structured data for organization and website
  generateEnhancedSchema({
    title: getSeoTitle(),
    description: getSeoDescription(),
    type: 'Organization',
    imageUrl: "https://future-level.ch/opengraph-image-p98pqg.png",
    canonicalUrl: canonicalUrl,
    language: language,
    keywords: getKeywords(),
    breadcrumbItems: [
      { name: language === 'de' ? 'Startseite' : language === 'en' ? 'Home' : language === 'fr' ? 'Accueil' : 'Home', url: canonicalUrl }
    ]
  });
  
  // Add service-specific schema markup using the industry schema generator
  generateIndustryServiceSchema(
    'general', // Use general service type for homepage
    getSeoTitle(),
    getSeoDescription(),
    "https://future-level.ch/opengraph-image-p98pqg.png",
    canonicalUrl,
    language
  );
};

/**
 * Sets up SEO for industry-specific pages
 * This function should be called on industry service pages
 */
export const setupIndustryPageSeo = (
  industry: 'finance' | 'luxury' | 'tourism' | 'healthcare' | 'ecommerce',
  language: string,
  city?: string
): void => {
  // Industry-specific titles
  const getTitleForIndustry = () => {
    const cityText = city ? ` ${city}` : '';
    
    switch (industry) {
      case 'finance':
        return language === 'de' ? `KI-Videomarketing für Finanzdienstleister${cityText} | Future Level` :
               language === 'en' ? `AI Video Marketing for Financial Services${cityText} | Future Level` :
               language === 'fr' ? `Marketing Vidéo IA pour Services Financiers${cityText} | Future Level` :
               `Marketing Video AI per Servizi Finanziari${cityText} | Future Level`;
      case 'luxury':
        return language === 'de' ? `KI-Videomarketing für Luxusmarken${cityText} | Future Level` :
               language === 'en' ? `AI Video Marketing for Luxury Brands${cityText} | Future Level` :
               language === 'fr' ? `Marketing Vidéo IA pour Marques de Luxe${cityText} | Future Level` :
               `Marketing Video AI per Marchi di Lusso${cityText} | Future Level`;
      case 'tourism':
        return language === 'de' ? `KI-Videomarketing für Tourismus${cityText} | Future Level` :
               language === 'en' ? `AI Video Marketing for Tourism${cityText} | Future Level` :
               language === 'fr' ? `Marketing Vidéo IA pour Tourisme${cityText} | Future Level` :
               `Marketing Video AI per Turismo${cityText} | Future Level`;
      case 'healthcare':
        return language === 'de' ? `KI-Videomarketing für Gesundheitswesen${cityText} | Future Level` :
               language === 'en' ? `AI Video Marketing for Healthcare${cityText} | Future Level` :
               language === 'fr' ? `Marketing Vidéo IA pour Santé${cityText} | Future Level` :
               `Marketing Video AI per Sanità${cityText} | Future Level`;
      case 'ecommerce':
        return language === 'de' ? `KI-Videomarketing für E-Commerce${cityText} | Future Level` :
               language === 'en' ? `AI Video Marketing for E-Commerce${cityText} | Future Level` :
               language === 'fr' ? `Marketing Vidéo IA pour E-Commerce${cityText} | Future Level` :
               `Marketing Video AI per E-Commerce${cityText} | Future Level`;
      default:
        return language === 'de' ? `KI-Videomarketing${cityText} | Future Level` :
               language === 'en' ? `AI Video Marketing${cityText} | Future Level` :
               language === 'fr' ? `Marketing Vidéo IA${cityText} | Future Level` :
               `Marketing Video AI${cityText} | Future Level`;
    }
  };
  
  // Industry-specific descriptions
  const getDescriptionForIndustry = () => {
    const cityText = city ? (
      language === 'de' ? ` in ${city}` :
      language === 'en' ? ` in ${city}` :
      language === 'fr' ? ` à ${city}` :
      ` a ${city}`
    ) : '';
    
    switch (industry) {
      case 'finance':
        return language === 'de' ? `Professionelle KI-generierte Videos für Banken, Versicherungen und Finanzberater${cityText}. Steigern Sie Vertrauen und Conversion mit maßgeschneiderten Finanz-Videos.` :
               language === 'en' ? `Professional AI-generated videos for banks, insurance companies, and financial advisors${cityText}. Increase trust and conversion with tailored financial videos.` :
               language === 'fr' ? `Vidéos professionnelles générées par IA pour banques, assurances et conseillers financiers${cityText}. Augmentez la confiance et la conversion avec des vidéos financières sur mesure.` :
               `Video professionali generati da IA per banche, assicurazioni e consulenti finanziari${cityText}. Aumenta la fiducia e la conversione con video finanziari personalizzati.`;
      case 'luxury':
        return language === 'de' ? `Premium KI-Videoinhalte für Luxusmarken und hochwertige Produkte${cityText}. Exklusiv für Schweizer Premiummarken mit Fokus auf Eleganz und visuelle Qualität.` :
               language === 'en' ? `Premium AI video content for luxury brands and high-end products${cityText}. Exclusively for Swiss premium brands focusing on elegance and visual quality.` :
               language === 'fr' ? `Contenu vidéo IA premium pour marques de luxe et produits haut de gamme${cityText}. Exclusivement pour les marques premium suisses axées sur l'élégance et la qualité visuelle.` :
               `Contenuto video AI premium per marchi di lusso e prodotti di alta gamma${cityText}. Esclusivamente per marchi premium svizzeri con focus su eleganza e qualità visiva.`;
      default:
        return language === 'de' ? `Professionelle KI-generierte Videos für effektives Marketing und höhere Conversion${cityText}. Future Level revolutioniert Ihren Content mit automatisierten Video-Lösungen.` :
               language === 'en' ? `Professional AI-generated videos for effective marketing and higher conversion${cityText}. Future Level revolutionizes your content with automated video solutions.` :
               language === 'fr' ? `Vidéos professionnelles générées par IA pour un marketing efficace et une meilleure conversion${cityText}. Future Level révolutionne votre contenu avec des solutions vidéo automatisées.` :
               `Video professionali generati da IA per marketing efficace e conversione più elevata${cityText}. Future Level rivoluziona i tuoi contenuti con soluzioni video automatizzate.`;
    }
  };
  
  // Industry-specific keywords
  const getKeywordsForIndustry = () => {
    const cityKeyword = city ? `, ${city}` : '';
    
    switch (industry) {
      case 'finance':
        return language === 'de' ? `KI-Videomarketing Finanzsektor${cityKeyword}, Banken Marketing Videos KI, Versicherung Erklärvideo, Finanzdienstleister KI-Videos, Fintech Videomarketing` :
               language === 'en' ? `AI video marketing financial sector${cityKeyword}, bank marketing videos AI, insurance explainer video, financial services AI videos, fintech video marketing` :
               language === 'fr' ? `Marketing vidéo IA secteur financier${cityKeyword}, vidéos marketing banques IA, vidéo explicative assurance, services financiers vidéos IA, marketing vidéo fintech` :
               `Marketing video AI settore finanziario${cityKeyword}, video marketing banche AI, video esplicativo assicurazione, servizi finanziari video AI, marketing video fintech`;
      case 'luxury':
        return language === 'de' ? `Luxusmarken KI-Videos${cityKeyword}, Premium Produktvideos, Hochwertiges Videomarketing, KI-Videos Schweizer Luxusmarken, Luxus Marketing AI` :
               language === 'en' ? `Luxury brands AI videos${cityKeyword}, premium product videos, high-end video marketing, AI videos Swiss luxury brands, luxury marketing AI` :
               language === 'fr' ? `Vidéos IA marques de luxe${cityKeyword}, vidéos produits premium, marketing vidéo haut de gamme, vidéos IA marques de luxe suisses, marketing de luxe IA` :
               `Video AI marchi di lusso${cityKeyword}, video prodotti premium, marketing video alta gamma, video AI marchi di lusso svizzeri, marketing lusso AI`;
      default:
        return language === 'de' ? `KI-Videomarketing${cityKeyword}, AI-generierte Videos, Marketing Conversion Videos, Automatisierte Videoerstellung, Content-Marketing KI, Social Media Videos Schweiz` :
               language === 'en' ? `AI video marketing${cityKeyword}, AI-generated videos, marketing conversion videos, automated video creation, content marketing AI, social media videos Switzerland` :
               language === 'fr' ? `Marketing vidéo IA${cityKeyword}, vidéos générées par IA, vidéos marketing conversion, création vidéo automatisée, marketing de contenu IA, vidéos médias sociaux Suisse` :
               `Marketing video AI${cityKeyword}, video generati da IA, video marketing conversione, creazione video automatizzata, marketing contenuti AI, video social media Svizzera`;
    }
  };
  
  // Construct the page URL with industry and possibly city
  const baseUrl = 'https://future-level-video-craft.lovable.app';
  
  let pagePath = '';
  switch (language) {
    case 'de':
      pagePath = `/de/leistungen/${industry}`;
      break;
    case 'en':
      pagePath = `/en/services/${industry}`;
      break;
    case 'fr':
      pagePath = `/fr/services/${industry}`;
      break;
    case 'it':
      pagePath = `/it/servizi/${industry}`;
      break;
    default:
      pagePath = `/en/services/${industry}`;
  }
  
  if (city) {
    pagePath += `/${city.toLowerCase()}`;
  }
  
  const canonicalUrl = `${baseUrl}${pagePath}`;
  
  // Update meta tags
  updateMetaTags({
    title: getTitleForIndustry(),
    description: getDescriptionForIndustry(),
    keywords: getKeywordsForIndustry(),
    canonicalUrl: canonicalUrl,
    ogImage: "https://future-level.ch/opengraph-image-p98pqg.png",
    ogType: "website",
    language: language
  });
  
  // Add service schema markup
  generateIndustryServiceSchema(
    industry,
    getTitleForIndustry(),
    getDescriptionForIndustry(),
    "https://future-level.ch/opengraph-image-p98pqg.png",
    canonicalUrl,
    language,
    city
  );
};
